import type { ISFPExtractedData } from '@/shared/types/isfp.types'

/**
 * Maps extracted iSFP data to a form-compatible structure.
 *
 * @param extractedData - The extracted iSFP data
 * @returns A partial ISFPExtractedData object suitable for form population
 */
export const mapExtractedDataToForm = (
  extractedData: ISFPExtractedData
): Partial<ISFPExtractedData> => {
  const {
    currentStateDescription,
    buildingInformation,
    energeticValues,
    substance,
    systemTechnology,
  } = extractedData ?? {}

  return {
    currentStateDescription: {
      lowerCompletion: currentStateDescription?.lowerCompletion,
      basementEntrance: currentStateDescription?.basementEntrance,
      exteriorWalls: currentStateDescription?.exteriorWalls,
      windows: currentStateDescription?.windows,
      upperCompletion: currentStateDescription?.upperCompletion,
      heating: currentStateDescription?.heating,
      heatingTransfer: currentStateDescription?.heatingTransfer,
      drinkingHotWater: currentStateDescription?.drinkingHotWater,
      ventilation: currentStateDescription?.ventilation,
    },

    buildingInformation: {
      constructionYear: buildingInformation?.constructionYear,
      numberOfResidentialUnits: buildingInformation?.numberOfResidentialUnits,
      heatedVolumeInCbm: buildingInformation?.heatedVolumeInCbm,
      usableSpaceInSqm: buildingInformation?.usableSpaceInSqm,
      numberOfFloors: buildingInformation?.numberOfFloors,
      meanFloorHeightInCm: buildingInformation?.meanFloorHeightInCm,
    },

    energeticValues: {
      finalEnergyDemandInKwh: energeticValues?.finalEnergyDemandInKwh,
      auxEnergyDemandInKwh: energeticValues?.auxEnergyDemandInKwh,
      energyConsumptionInKwh: energeticValues?.energyConsumptionInKwh,
      primaryEnergyDemandInKwh: energeticValues?.primaryEnergyDemandInKwh,
      transmissionHeatLossInWPerK: energeticValues?.transmissionHeatLossInWPerK,
      equivalentCo2EmissionsInTons: energeticValues?.equivalentCo2EmissionsInTons,
    },

    substance: {
      windowComponents: substance?.windowComponents ?? [],
      exteriorWallComponents: substance?.exteriorWallComponents ?? [],
      roofComponents: substance?.roofComponents ?? [],
      floorPlateComponents: substance?.floorPlateComponents ?? [],
      basementCeilingComponents: substance?.basementCeilingComponents ?? [],
      basementExteriorWallComponents: substance?.basementExteriorWallComponents ?? [],
      roofWindowComponents: substance?.roofWindowComponents ?? [],
      doorComponents: substance?.doorComponents ?? [],
      topFloorCeilingComponents: substance?.topFloorCeilingComponents ?? [],
    },

    systemTechnology: {
      heatingGeneration: systemTechnology?.heatingGeneration ?? [],
      drinkingHotWaterGeneration: systemTechnology?.drinkingHotWaterGeneration ?? [],
      heatingTransfer: systemTechnology?.heatingTransfer ?? [],
      ventilationSystem: systemTechnology?.ventilationSystem ?? [],
    },
  }
}
